import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import MobileMenu from './MobileMenu';
import authService from '../../services/authService';

const Header: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Handle Auth0 user login - only sync once when not already synced
  useEffect(() => {
    if (isAuthenticated && user) {
      const localUser = authService.getCurrentUser();
      if (!localUser) {
        // Only sync if user is not already synced to backend
        const syncUser = async () => {
          try {
            const accessToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
              }
            });
            await authService.loginWithAuth0(user, accessToken);
          } catch (err) {
            console.error('Failed to sync user with backend:', err);
          }
        };
        syncUser();
      }
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);
  
  // Handle window resize to detect mobile vs desktop view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  // Don't show the header on the game page for an immersive experience
  if (location.pathname === '/play') {
    return null;
  }
  
  return (
    <header className={`mobile-optimized-header ${menuOpen ? 'menu-open' : ''}`}>
      <div className="mobile-navbar-wrapper">
        <div className="navbar-content">
          <Link to="/" className="header-logo-container">
            <img src="/logo.jpeg" alt="KNOW Logo" className="header-logo-image" />
            <div className="header-logo-text">
              <div className="header-logo">KNOW</div>
              <div className="header-logo-by">By Emraay Games</div>
            </div>
          </Link>


          {/* Hamburger button for mobile */}
          <button
            onClick={toggleMenu}
            className="mobile-menu-toggle"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <div className={`hamburger-icon ${menuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>
        
        {/* Light glow effect */}
        <div className="navbar-glow"></div>
        
        {/* Desktop Navigation */}
        <nav className="desktop-navigation">
          <ul>
            <li>
              <Link 
                to="/" 
                className={location.pathname === '/' ? 'active' : ''}
              >
                Home
              </Link>
            </li>
            <li>
              <Link 
                to="/leaderboard" 
                className={location.pathname === '/leaderboard' ? 'active' : ''}
              >
                Leaderboard
              </Link>
            </li>
            <li>
              <Link 
                to="/ranks" 
                className={location.pathname === '/ranks' ? 'active' : ''}
              >
                Ranks
              </Link>
            </li>
            {isAuthenticated && (
              <>
                {/* 
                  TODO: Implement Favorites functionality
                  - Complete server-side favorites API endpoints on EC2 
                  - Integrate favorites-controller.js and favorites-routes.js with main server
                  - Update favoriteService.ts to use real API instead of mock data
                  - Test cross-platform sync between web and iOS apps
                  - Once API is working, uncomment the line below:
                */}
                {/* <li>
                  <Link 
                    to="/favorites" 
                    className={location.pathname === '/favorites' ? 'active' : ''}
                  >
                    Favorites
                  </Link>
                </li> */}
                <li>
                  <Link 
                    to="/profile" 
                    className={location.pathname === '/profile' ? 'active' : ''}
                  >
                    Profile
                  </Link>
                </li>
              </>
            )}
            <li>
              <Link 
                to="/about" 
                className={location.pathname === '/about' ? 'active' : ''}
              >
                About
              </Link>
            </li>
            <li>
              {!isLoading && (
                isAuthenticated ? (
                  <button 
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    className="auth-button logout-button"
                  >
                    Logout
                  </button>
                ) : (
                  <button 
                    onClick={() => loginWithRedirect()}
                    className="auth-button login-button"
                  >
                    Login
                  </button>
                )
              )}
            </li>
          </ul>
        </nav>

        {/* Mobile Navigation */}
        {menuOpen && isMobile && <MobileMenu onClose={() => setMenuOpen(false)} />}
      </div>
    </header>
  );
};

export default Header; 
